import {FULL_OWNERSHIP_ASSET_TYPE} from "@reva/shared/register";

export const typeParameter = "register_asset_type";
export const parcelParameter = "register_asset_parcel";
export const sizeParameter = "register_asset_size";
export const assetShareParameter = "register_asset_shares";
export const stockParameter = "register_asset_stock";
export const spaceParameter = "space_plot";
export const shareParameter = "ownership_share";

const DEFAULT_STOCK = 10000;

export interface AssetRow {
    [typeParameter]?: string | null;
    [parcelParameter]?: string | null;
    [sizeParameter]?: number | null;
    [assetShareParameter]?: number | null;
    [stockParameter]?: number | null;
    [field: string]: unknown;
}

const text = (value: unknown) => (value == null ? "" : String(value).trim());

function parcelKey(row: AssetRow | undefined): string | null {
    const parcel = text(row?.[parcelParameter]);
    return parcel === "" ? null : parcel;
}

const isSameParcel = (row1: AssetRow | undefined, row2: AssetRow | undefined) => {
    const key = parcelKey(row1);
    return key != null && key === parcelKey(row2);
};

const hasOtherRowsOfParcel = (assets: AssetRow[], index: number) =>
    assets.some((row, i) => i !== index && isSameParcel(row, assets[index]));

/** #18280: Bei gleichem Flurstück sind Größe und Bestand nur im ersten Eintrag editierbar und zählen nur einmal. */
function isFirstOfParcel(assets: AssetRow[], index: number): boolean {
    return !assets.slice(0, index).some((row) => isSameParcel(row, assets[index]));
}

export function calculateTotalSize(assets: AssetRow[]): number {
    return assets
        .filter((_, index) => isFirstOfParcel(assets, index))
        .reduce((sum, row) => sum + (row[sizeParameter] || 0), 0);
}

/** Miteigentumsanteil in Prozent, 0 wenn nicht berechenbar. */
export function calculateTotalShare(assets: AssetRow[]): number {
    const shareArea = assets.reduce((sum, row) => {
        const share = row[assetShareParameter];
        const stock = row[stockParameter];
        const size = row[sizeParameter];
        if (share == null || stock == null || size == null) return sum;
        if (row[typeParameter] === FULL_OWNERSHIP_ASSET_TYPE) return sum + size;
        // Anteil > Bestand zählt voll, damit die Summe 100 % übersteigt und die Spec-Grenze die Indikation stoppt
        return stock === 0 ? sum : sum + (share / stock) * size;
    }, 0);
    const result = (shareArea / calculateTotalSize(assets)) * 100;
    return Number.isNaN(result) ? 0 : Math.round(result * 10000) / 10000;
}

const SIZE_AFFECTING_FIELDS = [sizeParameter, parcelParameter];
const SHARE_AFFECTING_FIELDS = [sizeParameter, assetShareParameter, stockParameter, typeParameter, parcelParameter];

/**
 * Summen (space_plot, ownership_share) des neuen Bestands, die sich durch die Änderung von field ändern können.
 * @param assets vollständiger neuer Bestand ohne Platzhalterzeile
 * @param field geändertes Zeilenfeld; null = alle Summen (z.B. nach Zeile löschen)
 * @return nur die betroffenen Summen, leere Summen als null
 */
export function affectedTotals(assets: AssetRow[], field: string | null): Record<string, number | null> {
    const totals: Record<string, number | null> = {};
    if (field == null || SIZE_AFFECTING_FIELDS.includes(field)) {
        totals[spaceParameter] = calculateTotalSize(assets) || null;
    }
    if (field == null || SHARE_AFFECTING_FIELDS.includes(field)) {
        totals[shareParameter] = calculateTotalShare(assets) || null;
    }
    return totals;
}

/** Kaufmännisch auf 3 Nachkommastellen (#18295), über die Exponent-Schreibweise ohne Fließkommafehler (0.5005 * 1000 = 500.49999…). */
export const roundShares = (value: number | null | undefined) =>
    value == null ? value : Number(`${Math.round(Number(`${value}e3`))}e-3`);

export function isLockedByParcel(assets: AssetRow[], index: number, field: string): boolean {
    return (field === sizeParameter || field === stockParameter) && !isFirstOfParcel(assets, index);
}

export function isFieldDisabled(assets: AssetRow[], index: number, field: string): boolean {
    return (
        isLockedByParcel(assets, index, field) ||
        (assets[index]?.[typeParameter] === FULL_OWNERSHIP_ASSET_TYPE &&
            (field === assetShareParameter || field === stockParameter))
    );
}

function sharesOnTypeChange(assets: AssetRow[], index: number, type: unknown): AssetRow {
    const stock = hasOtherRowsOfParcel(assets, index)
        ? (assets[index][stockParameter] ?? DEFAULT_STOCK)
        : DEFAULT_STOCK;
    return {[assetShareParameter]: type === FULL_OWNERSHIP_ASSET_TYPE ? stock : 0, [stockParameter]: stock};
}

/**
 * Neuer Bestand nach Änderung eines Felds, oder null, wenn sich nichts ändert.
 * index === assets.length legt einen neuen Eintrag an.
 */
export function updateAssetRow(assets: AssetRow[], index: number, field: string, value: unknown): AssetRow[] | null {
    const current = assets[index];
    if (current === undefined ? !value : (current[field] == null && value === "") || current[field] === value) {
        return null;
    }

    const shares = field === typeParameter ? sharesOnTypeChange(assets, index, value) : {};
    let updated: AssetRow = {...current, ...shares, [field]: value};
    const result = [...assets];
    result[index] = updated;

    if (field === sizeParameter || field === stockParameter) {
        return result.map((row, i) => (i !== index && isSameParcel(row, updated) ? {...row, [field]: value} : row));
    }
    if (field === parcelParameter) {
        const sameParcelRow = result.find((row, i) => i !== index && isSameParcel(row, updated));
        if (sameParcelRow) {
            updated = {
                ...updated,
                [sizeParameter]: sameParcelRow[sizeParameter],
                [stockParameter]: sameParcelRow[stockParameter],
            };
            result[index] = updated;
        }
    }
    return result;
}

export function deleteAssetRow(assets: AssetRow[], index: number): AssetRow[] {
    return assets.filter((_, i) => i !== index);
}

export type AssetProblemKind = "full_ownership_shared" | "shares_exceeded" | "inconsistent" | "total_share_exceeded";

export interface AssetProblem {
    kind: AssetProblemKind;
    /** Indizes der Einträge des betroffenen Flurstücks (ohne Flurstück: nur der Eintrag selbst; Gesamtanteil: keine). */
    rows: number[];
    parcel: string;
}

/** Felder, die bei einem Problem rot markiert werden. */
export const PROBLEM_FIELDS: Record<AssetProblemKind, string[]> = {
    full_ownership_shared: [typeParameter],
    shares_exceeded: [assetShareParameter],
    inconsistent: [sizeParameter, stockParameter],
    total_share_exceeded: [],
};

/** Anteil eines Eintrags am Flurstück (1 = ganzes Flurstück). */
function ownedFraction(row: AssetRow): number {
    if (row[typeParameter] === FULL_OWNERSHIP_ASSET_TYPE) return 1;
    const share = row[assetShareParameter] ?? 0;
    const stock = row[stockParameter];
    if (stock == null || share === 0) return 0;
    return stock === 0 ? Number.POSITIVE_INFINITY : share / stock;
}

const differ = (rows: AssetRow[], field: string) => new Set(rows.map((row) => row[field] ?? null)).size > 1;

export function findAssetProblems(assets: AssetRow[]): AssetProblem[] {
    const groups = new Map<string, number[]>();
    assets.forEach((row, index) => {
        const key = parcelKey(row) ?? `row:${index}`;
        groups.set(key, [...(groups.get(key) ?? []), index]);
    });

    const parcelProblems = [...groups.values()].flatMap((indices) => {
        const rows = indices.map((index) => assets[index]);
        const problem = (kind: AssetProblemKind): AssetProblem => ({
            kind,
            rows: indices,
            parcel: text(rows[0][parcelParameter]),
        });
        const problems: AssetProblem[] = [];
        if (rows.length > 1 && rows.some((row) => row[typeParameter] === FULL_OWNERSHIP_ASSET_TYPE)) {
            problems.push(problem("full_ownership_shared"));
        } else if (rows.reduce((sum, row) => sum + ownedFraction(row), 0) > 1 + 1e-9) {
            problems.push(problem("shares_exceeded"));
        }
        if (rows.length > 1 && (differ(rows, sizeParameter) || differ(rows, stockParameter))) {
            problems.push(problem("inconsistent"));
        }
        return problems;
    });
    // Die Flurstück-Meldung erklärt den Überschuss bereits genauer
    const sharesExplained = parcelProblems.some((problem) => problem.kind !== "inconsistent");
    return !sharesExplained && calculateTotalShare(assets) > 100
        ? [...parcelProblems, {kind: "total_share_exceeded", rows: [], parcel: ""}]
        : parcelProblems;
}
